import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useUrlFilters } from '../lib/useUrlFilters';
import { labelForGran } from '../lib/periodFormat';
import Breadcrumb from './nav/Breadcrumb';
import FilterBar from './nav/FilterBar';
import EmptyState from './nav/EmptyState';
import RecurrenceEditor from './nav/RecurrenceEditor';
import PageSkeleton from './nav/PageSkeleton';

import { usePeriodoData } from './periodo/usePeriodoData';
import { usePeriodoGroups } from './periodo/usePeriodoGroups';
import { usePeriodoFiltered } from './periodo/usePeriodoFiltered';
import { usePeriodoMutations } from './periodo/usePeriodoMutations';
import PeriodoHeader from './periodo/PeriodoHeader';
import PeriodoQuickAddBar from './periodo/PeriodoQuickAddBar';
import TargetNumericiSection from './periodo/TargetNumericiSection';
import TappeSection from './periodo/TappeSection';
import MitSection from './periodo/MitSection';
import AzioniSection from './periodo/AzioniSection';
import DailyTasksSection from './periodo/DailyTasksSection';

/**
 * PaginaPeriodo — vista "periodo" (anno/quarter/mese/settimana/giorno).
 *
 * Orchestratore puro. Tutta la logica è incapsulata in hook condivisi sotto
 * `./periodo/`:
 *   - data fetch + optimistic toggle  → usePeriodoData
 *   - raggruppamenti memoizzati       → usePeriodoGroups
 *   - filtri trasversali + derivati   → usePeriodoFiltered
 *   - mutation assign/reassign        → usePeriodoMutations
 *
 * Refactor Feb 2026: da ~432 righe → ~165. Complessità sotto 10.
 */
export default function PaginaPeriodo() {
  const { gran, label } = useParams();
  const [search] = useSearchParams();
  const krFilter = search.get('kr');

  const [filters, setFilters] = useUrlFilters();
  const [collapsed, setCollapsed] = useState({});
  const [recurrenceEditing, setRecurrenceEditing] = useState(null);
  const toggleCollapsed = (kind, key) =>
    setCollapsed((c) => ({ ...c, [`${kind}:${key}`]: !c[`${kind}:${key}`] }));
  const isCollapsed = (kind, key) => !!collapsed[`${kind}:${key}`];

  const {
    data, loading, error,
    azioni, setAzioni, mit, setMit, targets,
    load,
    toggleMitItem, toggleAzioneItem, toggleTappaItem,
  } = usePeriodoData(gran, label, krFilter);

  const { mitByData, mitByArea, azioniByArea, sevenDayBuckets } = usePeriodoGroups({
    data, azioni, mit, gran, label,
  });

  // Filtri + derivati (exec, target, liste filtrate). Hook chiamato anche se
  // `data` è null (durante loading) per rispettare le regole degli hooks; gli
  // output vengono usati solo se `data` esiste.
  const filtered = usePeriodoFiltered({
    data: data || { key_results: [] },
    gran, mit, azioni, targets, sevenDayBuckets,
    filters, setFilters,
    mitByData, mitByArea, azioniByArea,
  });

  const { assignDate, reassignTask, updateText, deleteTask } = usePeriodoMutations({ label, load });

  // ─── Loading / Error ────────────────────────────────────────────────────
  if (loading) {
    const variant = gran === 'anno' ? 'grid' : 'list';
    const sections = gran === 'settimana' ? 4 : 3;
    return <PageSkeleton variant={variant} sections={sections} rowsPerSection={3} testid="periodo-skeleton" />;
  }
  if (error || !data) {
    return <PeriodoError gran={gran} label={label} error={error} onRetry={load} />;
  }

  // ─── Render principale ─────────────────────────────────────────────────
  const titolo = labelForGran(gran, label);
  const filtroKr = krFilter ? filtered.krById[krFilter] : null;
  const isEmpty =
    filtered.filteredAzioni.length === 0 &&
    filtered.filteredMit.length === 0 &&
    filtered.tappaTargets.length === 0 &&
    filtered.numericoTargets.length === 0;

  return (
    <div className="min-h-screen bg-canvas text-ink">
      <div className="max-w-7xl mx-auto px-6 pt-2">
        <Breadcrumb
          backTo={filtroKr ? `/obiettivo/${filtroKr.obiettivo_id}` : '/'}
          backLabel={filtroKr ? 'Obiettivo' : 'Dashboard'}
          items={[
            { label: 'Dashboard', to: '/' },
            ...(filtroKr ? [
              { label: `Obiettivo ${String(filtroKr.obiettivo_ordine).padStart(2, '0')}`, to: `/obiettivo/${filtroKr.obiettivo_id}` },
              { label: `KR · ${filtroKr.titolo}` },
            ] : []),
            { label: titolo },
          ]}
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 pb-20">
        <PeriodoHeader
          gran={gran}
          label={label}
          titolo={titolo}
          filtroKr={filtroKr}
          exec={filtered.exec}
          krs={filtered.krs}
          azioni={azioni}
          setAzioni={setAzioni}
          setMit={setMit}
        />

        <FilterBar filters={filters} onChange={setFilters} />
        <PeriodoQuickAddBar gran={gran} label={label} />

        <TargetNumericiSection targets={filtered.numericoTargets} krById={filtered.krById} />
        <TappeSection
          targets={filtered.tappaTargets}
          krById={filtered.krById}
          onToggle={toggleTappaItem}
          onChipAreaClick={filtered.toggleAreaChip}
        />

        {gran === 'settimana' ? (
          <DailyTasksSection
            sevenDayBuckets={sevenDayBuckets}
            filteredMit={filtered.filteredMit}
            filteredAzioni={filtered.filteredAzioni}
            mit={mit}
            azioni={azioni}
            filtersActive={filtered.filtersActive}
            krById={filtered.krById}
            isCollapsed={isCollapsed}
            toggleCollapsed={toggleCollapsed}
            onToggleMit={toggleMitItem}
            onToggleAzione={toggleAzioneItem}
            onChipAreaClick={filtered.toggleAreaChip}
            onPromote={load}
            onEditRecurrence={setRecurrenceEditing}
            onAssignDate={assignDate}
            onReassignTask={reassignTask}
            onUpdateText={updateText}
            onDeleteTask={deleteTask}
            onDemoted={load}
          />
        ) : (
          <>
            <MitSection
              gran={gran}
              filteredMit={filtered.filteredMit}
              mit={mit}
              filtersActive={filtered.filtersActive}
              filteredMitByArea={filtered.filteredMitByArea}
              filteredMitByData={filtered.filteredMitByData}
              filteredMitBySevenDays={filtered.filteredMitBySevenDays}
              krById={filtered.krById}
              isCollapsed={isCollapsed}
              toggleCollapsed={toggleCollapsed}
              onToggle={toggleMitItem}
              onChipAreaClick={filtered.toggleAreaChip}
              onEditRecurrence={setRecurrenceEditing}
              onUpdateText={updateText}
              onDeleteTask={deleteTask}
              onAssignDate={assignDate}
              onDemoted={load}
            />
            <AzioniSection
              filteredAzioni={filtered.filteredAzioni}
              azioni={azioni}
              filtersActive={filtered.filtersActive}
              filteredAzioniByArea={filtered.filteredAzioniByArea}
              krById={filtered.krById}
              isCollapsed={isCollapsed}
              toggleCollapsed={toggleCollapsed}
              onToggle={toggleAzioneItem}
              onChipAreaClick={filtered.toggleAreaChip}
              onPromote={load}
              onEditRecurrence={setRecurrenceEditing}
              onAssignDate={assignDate}
              onUpdateText={updateText}
              onDeleteTask={deleteTask}
            />
          </>
        )}

        {isEmpty && <PeriodoEmpty gran={gran} filtersActive={filtered.filtersActive} />}
      </div>

      {recurrenceEditing && (
        <RecurrenceEditor
          parent={recurrenceEditing}
          onClose={() => setRecurrenceEditing(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}

// ── Sotto-componenti pure (estratti per leggibilità) ──────────────────────

function PeriodoError({ gran, label, error, onRetry }) {
  return (
    <div className="min-h-screen bg-canvas text-ink">
      <div className="max-w-7xl mx-auto px-6 pt-2">
        <Breadcrumb
          backTo="/"
          backLabel="Dashboard"
          items={[{ label: 'Dashboard', to: '/' }, { label: `${gran} · ${label}` }]}
        />
      </div>
      <div className="max-w-3xl mx-auto px-6 py-16">
        <h1 className="font-display text-3xl tracking-tight mb-3">Impossibile caricare il periodo</h1>
        <p className="font-body text-sm text-soft mb-6">{error || 'Risposta vuota dal server.'}</p>
        <button
          onClick={onRetry}
          className="font-mono text-xs uppercase tracking-[0.18em] px-4 py-2 border border-ink hover:bg-ink hover:text-canvas transition-colors"
          data-testid="periodo-retry-btn"
        >
          Riprova
        </button>
      </div>
    </div>
  );
}

function PeriodoEmpty({ gran, filtersActive }) {
  if (filtersActive) {
    return (
      <div className="border border-dashed border-line p-10 text-center font-body text-sm text-mute" data-testid="periodo-empty">
        Nessun elemento soddisfa i filtri attivi. Pulisci i filtri per vedere tutto.
      </div>
    );
  }
  const granLabel = gran === 'mese' ? 'mese'
    : gran === 'quarter' ? 'trimestre'
      : gran === 'settimana' ? 'settimana'
        : 'periodo';
  // CTA verso la chat della granularità corrente, non sempre /mese
  const coachByGran = {
    quarter: { to: '/trimestre', label: 'Pianifica il trimestre con il coach' },
    mese: { to: '/mese', label: 'Pianifica il mese con il coach' },
    settimana: { to: '/settimana', label: 'Pianifica la settimana con il coach' },
    giorno: { to: '/settimana', label: 'Pianifica la settimana con il coach' },
    anno: { to: '/setup', label: 'Fai il setup con il coach' },
  };
  const coach = coachByGran[gran] || coachByGran.mese;
  const desc = gran === 'mese' || gran === 'quarter'
    ? 'Non hai ancora target periodi né MIT né azioni pianificate. Pianifica con il coach o aggiungi una MIT al volo.'
    : 'Non ci sono MIT o azioni per questo periodo. Premi ⇧N per aggiungerne una al volo.';
  return (
    <EmptyState
      title={`Niente in questo ${granLabel}`}
      desc={desc}
      actionLabel={coach.label}
      actionTo={coach.to}
      secondary={{
        label: 'Aggiungi al volo (⇧N)',
        onClick: () => window.dispatchEvent(new CustomEvent('qp:quick-add-open')),
      }}
      testid="periodo-empty"
    />
  );
}
